import React, { useState, ChangeEvent, useEffect } from "react";
import { TextInput } from "../TextInput";
import {
  sanitiseString,
  concatStringFromClassesArrayList,
} from "../../Utils/StringUtils";
import { AutosuggestResultsContainer } from "../../Styles/Autosuggest/Containers";

export interface Props {
  id: string;
  label: string;
  hint?: string;
  onSelect?: (...args: any[]) => void;
  autosuggestData: SuggestData[];
  dataTestId?: string;
  minChars?: number;
  limitSuggestions?: number;
  classes?: string;
  instructions?: string;
  ariaYouHaveSelected?: string;
  ariaMinChars?: string;
  ariaOneResult?: string;
  ariaNResults?: string;
  ariaLimitedResults?: string;
  moreResults?: string;
  resultsTitle?: string;
  noResults?: string;
  typeMore?: string;
  selectedValue?: SuggestData;
  allowFreeText?: boolean;
}

interface SuggestData {
  name: string;
  code: string;
}

export const Autosuggest = (props: Props) => {
  const classList = {
    defaultClass: "ons-autosuggest",
    optionalClasses: [props.classes || null],
  };

  const className = concatStringFromClassesArrayList(classList);

  const sanitisedId = sanitiseString(props.id);
  const sanitisedDataTestId = sanitiseString(props.dataTestId);

  const minChars = props.minChars || 3;

  const allowFreeText =
    props.allowFreeText !== undefined ? props.allowFreeText : true;

  const limitSuggestions = props.limitSuggestions || 10;

  const initialSuggestions: SuggestData[] = [];

  const [search, setSearch] = useState({
    text: props.selectedValue || { name: "", code: "" },
    suggestions: initialSuggestions,
  });

  const [selectedIndex, setSelectedIndex] = useState(-1);

  const matchPattern = new RegExp(search.text.name, "i");

  const [isComponentVisible, setIsComponentVisible] = useState(false);

  const [moreSuggestions, setMoreSuggestions] = useState(false);

  useEffect(() => {
    setSearch({
      text: props.selectedValue || { name: "", code: "" },
      suggestions: [],
    });
  }, [props.selectedValue]);

  const onTextChanged = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    const regex = new RegExp(value, "i");

    const allSuggestions: SuggestData[] = props.autosuggestData.filter(
      (v: SuggestData) => regex.test(v.name)
    );
    let filteredSuggestions: SuggestData[] = [];

    if (value.length >= minChars) {
      filteredSuggestions = allSuggestions.slice(0, limitSuggestions);

      setIsComponentVisible(true);
    } else {
      setIsComponentVisible(false);
    }

    setSearch({
      text: { name: value, code: "user-input" },
      suggestions: filteredSuggestions,
    });

    setMoreSuggestions(allSuggestions.length > filteredSuggestions.length);
    setSelectedIndex(-1);
  };

  const handleBlur = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setIsComponentVisible(false);
    if (
      search.text.name !== "" &&
      (search.text.code === "" || search.text.code === "user-input")
    ) {
      if (allowFreeText) {
        props.onSelect?.({ name: event.target.value, code: "user-input" });
      } else {
        setSearch({
          text: { name: "", code: "" },
          suggestions: [],
        });
        props.onSelect?.({ name: "", code: "" });
      }
    }
  };

  const suggestionSelected = (event: React.MouseEvent, value: SuggestData) => {
    if (event.button === 0) {
      setIsComponentVisible(false);

      setSearch({
        text: value,
        suggestions: [],
      });

      props.onSelect?.(value);
    }
  };

  const { suggestions } = search;

  const showSuggestions = () => {
    if (isComponentVisible) {
      return { display: "block" };
    } else {
      return { display: "none" };
    }
  };

  const showSuggestionsTitle = () => {
    if (suggestions.length === 0) {
      return { display: "none" };
    } else {
      return { display: "block" };
    }
  };

  const makeBold = (text: string) => {
    const startIndex = text.search(matchPattern);
    const endIndex = startIndex + search.text.name.length;

    return (
      <>
        {`${text.slice(0, startIndex)}`}
        <strong>{text.slice(startIndex, endIndex)}</strong>
        {text.slice(endIndex)}
      </>
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (selectedIndex < suggestions.length - 1) {
        setSelectedIndex(selectedIndex + 1);
      }
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (selectedIndex > 0) {
        setSelectedIndex(selectedIndex - 1);
      }
    }

    if (event.key === "Enter") {
      event.preventDefault();

      if (selectedIndex >= 0) {
        setSearch({
          text: search.suggestions[selectedIndex],
          suggestions: [],
        });

        setIsComponentVisible(false);
        props.onSelect?.(search.suggestions[selectedIndex]);
      }
    }
  };

  const handleMouseOver = (event: React.MouseEvent, index: number) => {
    event.preventDefault();
    setSelectedIndex(index);
  };

  const focusClass = (index: number) => {
    let className = "";
    if (index === selectedIndex) {
      className = "ons-autosuggest__option ons-autosuggest__option--focused";
    } else {
      className = "ons-autosuggest__option";
    }
    return className;
  };

  const defaultInstructions = `Use up and down keys to navigate suggestions once you've typed more than ${
    minChars - 1
  } characters. Use the enter key to select a suggestion. Touch device users, explore by touch or with swipe gestures.`;
  const defaultAriaYouHaveSelected = "You have selected";
  const defaultAriaMinChars = `Enter ${minChars} or more characters for suggestions.`;
  const defaultAriaOneResult = "There is one suggestion available.";
  const defaultAriaNResults = `There are ${suggestions.length} suggestions available.`;
  const defaultAriaLimitedResults = `Results have been limited to ${limitSuggestions} suggestions. Type more characters to improve your search`;
  const defaultMoreResults = "Continue entering to improve suggestions";
  const defaultResultsTitle = "Suggestions";
  const defaultNoResults =
    "No suggestions found. You can enter your own answer";
  const defaultTypeMore = "Continue entering to get suggestions";

  return (
    <div
      id={`${sanitisedId}-container`}
      data-testid={sanitisedDataTestId}
      className={className}
      onKeyDown={(e) => handleKeyDown(e)}
      data-instructions={props.instructions || defaultInstructions}
      data-aria-you-have-selected={
        props.ariaYouHaveSelected || defaultAriaYouHaveSelected
      }
      data-min-chars={minChars}
      data-aria-min-chars={props.ariaMinChars || defaultAriaMinChars}
      data-aria-one-result={props.ariaOneResult || defaultAriaOneResult}
      data-aria-n-results={props.ariaNResults || defaultAriaNResults}
      data-aria-limited-results={
        props.ariaLimitedResults || defaultAriaLimitedResults
      }
      data-more-results={props.moreResults || defaultMoreResults}
      data-results-title={props.resultsTitle || defaultResultsTitle}
      data-no-results={props.noResults || defaultNoResults}
      data-type-more={props.typeMore || defaultTypeMore}
    >
      <TextInput
        id={`${sanitisedId}-input`}
        label={{
          text: props.label,
          ...(props.hint ? { description: props.hint } : {}),
          id: `${sanitisedId}-input-label`,
        }}
        type="text"
        autoFocus
        autoComplete="off"
        dataTestId={`${sanitisedDataTestId}-input`}
        value={search.text.name}
        onChange={(e) => onTextChanged(e)}
        onBlur={(e) => handleBlur(e)}
      />
      <AutosuggestResultsContainer
        className="ons-autosuggest__results ons-input--w-20"
        style={showSuggestions()}
      >
        <div
          id="suggestions"
          className="ons-autosuggest__results-title ons-u-fs-s"
          style={showSuggestionsTitle()}
        >
          {props.resultsTitle || defaultResultsTitle}
        </div>

        <ul
          className="ons-autosuggest__listbox"
          title="Suggestions"
          role="listbox"
          id={`${sanitisedId}-listbox`}
          data-testid={`${sanitisedDataTestId}-listbox`}
          tabIndex={-1}
          style={showSuggestions()}
        >
          {suggestions.length > 0 && isComponentVisible ? (
            suggestions.map((item: SuggestData, index) => (
              <li
                key={item.code}
                onMouseDown={(e) => e.preventDefault()}
                onMouseUp={(e) => suggestionSelected(e, item)}
                onMouseOver={(e) => handleMouseOver(e, index)}
                className={focusClass(index)}
                id={`${sanitisedId}-listbox__option--${index.toString()}`}
                data-testid={`${sanitisedDataTestId}-listbox__option--${index.toString()}`}
                role="option"
                aria-selected={index === selectedIndex ? "true" : "false"}
              >
                {makeBold(item.name)}
              </li>
            ))
          ) : (
            <li className="ons-autosuggest__option ons-autosuggest__option--no-results">
              {props.noResults || defaultNoResults}
            </li>
          )}
          {moreSuggestions && (
            <li
              className="ons-autosuggest__option ons-autosuggest__option--more-results ons-u-fs-s"
              aria-hidden="true"
            >
              {props.moreResults || defaultMoreResults}
            </li>
          )}
        </ul>
      </AutosuggestResultsContainer>
      <div
        className="ons-autosuggest__instructions ons-u-vh"
        id={`${sanitisedId}-instructions`}
        tabIndex={-1}
      >
        {props.instructions || defaultInstructions}
      </div>
      <div
        className="ons-autosuggest__status ons-u-vh"
        aria-live="assertive"
        aria-atomic="true"
        role="status"
        tabIndex={-1}
      ></div>
    </div>
  );
};
